import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
// V4.0: the DOCX-native editor. Vendored/linked package @propdocs-editor/react.
// This import is code-split (the whole container is React.lazy'd by EditorSwitch),
// so the bundle only loads for docs flagged editor_engine='docx_native'.
import {
  DocxEditor,
  type DocxEditorRef,
  type EditorMode,
  createEmptyDocument,
  buildPlaceholderNode,
  buildCheckboxNode,
} from '@propdocs-editor/react';
import '@propdocs-editor/react/styles.css';
// Re-points the editor's --doc-* tokens at PropDocs brand values. MUST come
// after the package stylesheet so these overrides win.
import '../../../../styles/docx-editor-brand.css';
import { TextSelection } from 'prosemirror-state';
import { useAuth } from '../../../../auth';
import { queries, mutations } from '../../../../api';
import { updateDocumentTemplateDraft } from '../../../../utils/api/update-document-offer/update-document';
import { useNotifications } from '../../../../notifications';
import type { TemplateEditorContainerPropTypes } from '../utils/document-editor.types';
import { PlaceholderPopup, requestPlaceholderPopupOpen } from './PlaceholderPopup';
import { CLAUSE_DND_MIME, decodeClauseDnd, type ClauseDndPayload } from './clauseDnd';
import { registerDocxNativeEditor, makeHandleFromRef } from './docxNativeEditorRegistry';
import { encodeAuthorToken } from './authorToken';
import { useDocxSuggestionSync } from './useDocxSuggestionSync';
// Shared with the deal container — one body resolver for both editors.
import { useResolvedDocxBody } from './useResolvedDocxBody';
import {
  restrictedEditingPlugin,
  setRestrictedContext,
  makeSelectionEditable,
  isRangeEditable,
} from './plugins/restrictedEditing';

const AUTOSAVE_DEBOUNCE_MS = 1500;

// Derive the editor's document input types straight from the component so the
// container tracks the real package types without importing core's internal
// subpaths (which classic `node` module resolution can't resolve).
type DocxEditorPropsT = React.ComponentProps<typeof DocxEditor>;
type EditorDocument = DocxEditorPropsT['document'];
type EditorDocumentBuffer = DocxEditorPropsT['documentBuffer'];

/**
 * DOCX-native template editor container — the V4.0 replacement for
 * `TemplateEditorContainer` (CKEditor). It satisfies the SAME prop contract so
 * `EditorSwitch` can swap it in behind the flag with the existing template UI
 * (publishing, sidenav, revision switcher) unchanged around it.
 *
 * Body source, by `body_storage_format` (inside the template body JSON):
 *   - 'json'  → the template body IS the editor's Document-model JSON: pass as
 *               `document` (no conversion — the native path).
 *   - 'html'  → legacy CKEditor HTML not yet migrated: run it through the
 *               migration converter (docx-migration-v2) to a .docx buffer and
 *               pass as `documentBuffer`. (A doc is normally migrated BEFORE its
 *               flag flips, so this branch is the on-the-fly fallback.)
 *
 * The redundant separate header/footer editor is NOT rendered here — the editor
 * edits header/footer inline.
 */
export const DocxNativeTemplateEditorContainer: React.FC<TemplateEditorContainerPropTypes> = ({
  documentTemplate,
  documentId,
  teamId,
  canEdit,
  isRestrictedEditingEnabled,
  disableWordExport,
  editorConfiguration,
  editorMode,
  onDocumentChange,
  onSuggestionChange,
  onDraftSaved,
  onRefetchTemplate,
}) => {
  const editorRef = useRef<DocxEditorRef>(null);
  const apolloClient = useApolloClient();
  const [, { error: notifyError, success: notifySuccess }] = useNotifications() as any;
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Header/footer LOCK (PropDocs `header_restricted_editing`/`footer_restricted_editing`).
  // A locked H/F can't be entered for editing and shows a 🔒 badge. Seeded from
  // the template config; kept in local state so the toggle is instant (the editor
  // body is frozen on mount and a config refetch wouldn't re-render the lock).
  // NOTE: `documentTemplate` is the CONTENT hash (body object) — it never has
  // an `editorConfiguration` key. The config arrives via its own prop; the old
  // read (`documentTemplate.editorConfiguration`) silently produced {} and no
  // migrated template ever seeded its header/footer locks.
  const cfg = editorConfiguration ?? {};
  const [headerLocked, setHeaderLocked] = useState<boolean>(!!cfg.header_restricted_editing);
  const [footerLocked, setFooterLocked] = useState<boolean>(!!cfg.footer_restricted_editing);
  const [updateTemplateEditorConfiguration] = useMutation(mutations.updateTemplateEditorConfiguration);

  const setHeaderFooterLock = useCallback(
    (position: 'header' | 'footer', next: boolean) => {
      if (position === 'header') setHeaderLocked(next);
      else setFooterLocked(next);
      updateTemplateEditorConfiguration({
        variables: {
          templateId: String(documentId),
          configuration: position === 'header' ? { headerRestrictedEditing: next } : { footerRestrictedEditing: next },
        },
      }).catch(() => {
        // Roll the optimistic flip back — otherwise the chrome says "locked for
        // deals" while nothing was persisted, and the state silently disagrees
        // with the deal documents the flag actually governs.
        if (position === 'header') setHeaderLocked(!next);
        else setFooterLocked(!next);
        notifyError({
          message: `Could not ${next ? 'lock' : 'unlock'} the ${position}. Please try again.`,
        });
      });
    },
    [documentId, updateTemplateEditorConfiguration, notifyError]
  );

  // Lock toggle from the inline H/F editor's Options menu (region is unlocked).
  const onToggleHeaderFooterLock = useCallback(
    (position: 'header' | 'footer') => {
      setHeaderFooterLock(position, position === 'header' ? !headerLocked : !footerLocked);
    },
    [headerLocked, footerLocked, setHeaderFooterLock]
  );

  // Double-click on a LOCKED header/footer → unlock it directly (no confirm —
  // it's trivially reversible). This is the only way to reach the unlock (a
  // locked region can't open its own editor).
  const onRequestHeaderFooterUnlock = useCallback(
    (position: 'header' | 'footer') => {
      setHeaderFooterLock(position, false);
    },
    [setHeaderFooterLock]
  );

  // Current user drives suggestion/comment authorship (was defaulting to "User"
  // for everyone). `useAuth` is the same source the CKEditor container uses.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [{ user }] = useAuth() as any;
  const displayName: string = user?.fullName || user?.email || 'User';
  // Encode the acting user's id into the tracked-change author token (same as
  // the deal editor) so template suggestions carry authorId through OOXML.
  const author = useMemo(
    () => encodeAuthorToken({ authorId: user?.id ? String(user.id) : undefined, display: displayName }),
    [user?.id, displayName]
  );

  // Project the template draft's live tracked changes into `document_suggestions`
  // so the publish gate (has_pending_suggestions?) + counts see them.
  const suggestionSync = useDocxSuggestionSync(documentId ? String(documentId) : undefined, 'DocumentTemplate');

  // Team members → @-mention feed for comments/suggestion replies. Same shape
  // the editor's `mentionableUsers` prop expects ({ userId, handle, display }).
  const { data: teamData } = useQuery(queries.getTeamById, {
    skip: teamId == null,
    variables: { teamId },
  });
  const mentionableUsers = useMemo(() => {
    const members = teamData?.teamById?.timelineMembers ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return members.map((m: any) => ({
      userId: String(m.id),
      handle: (m.email || m.fullName || '').split('@')[0] || String(m.id),
      display: m.fullName || m.email || String(m.id),
    }));
  }, [teamData]);

  // PropDocs restricted-editing plugin (its own copy of the CKE RestrictedEditing
  // model). One instance for the editor's lifetime.
  const externalPlugins = useMemo(() => [restrictedEditingPlugin()], []);

  // Map PropDocs' editorMode/permissions → the editor's mode.
  // Templates are ALWAYS suggestion mode (PropDocs templates have no
  // editing/suggesting toggle — every authoring change is a tracked suggestion
  // so reviewers can accept/reject). Read-only when the user can't edit or is
  // viewing the published version. The mode is PINNED via `lockMode` below so
  // the dropdown is a locked badge, not a switch.
  const mode: EditorMode = useMemo(() => {
    if (!canEdit || editorMode === 'published') return 'viewing';
    return 'suggesting';
  }, [canEdit, editorMode]);
  // Pin the mode in both suggesting (templates never toggle) and viewing
  // (published/read-only) — the dropdown shows a locked badge, not a switch.
  const modeLocked = mode === 'suggesting' || mode === 'viewing';

  // Bumped after an import so the editor re-resolves onto the new body
  // immediately (the body is otherwise frozen on mount to protect live edits).
  const [reloadKey, setReloadKey] = useState(0);
  // Tracks the body we imported so we can bump `reloadKey` the moment the
  // refetched source actually carries it (avoids re-freezing on the stale body).
  const pendingImportBodyRef = useRef<string | null>(null);
  const incomingBody: string | undefined = (documentTemplate as any)?.body ?? (documentTemplate as any)?.template?.body;
  useEffect(() => {
    if (pendingImportBodyRef.current !== null && incomingBody === pendingImportBodyRef.current) {
      pendingImportBodyRef.current = null;
      setReloadKey((k) => k + 1);
    }
  }, [incomingBody]);

  // The body: model-JSON (native), a migrated .docx buffer, or a converted
  // buffer (legacy HTML run through the migration converter on open).
  const { document, documentBuffer, resolving } = useResolvedDocxBody<EditorDocument, EditorDocumentBuffer>(
    documentTemplate,
    reloadKey
  );

  // Autosave: on change, serialize via the editor's OWN save() → .docx bytes and
  // persist base64 with body_storage_format='docx'. We use .docx (not raw model
  // JSON) because the model holds Maps (headers/media) that JSON.stringify would
  // flatten to {} — breaking reload. save() is the lossless OOXML round-trip
  // (29/29 gate). Debounced: one row UPDATE per pause, no S3.
  // A save in flight must NOT block the user or trigger another save mid-flight.
  // We track the in-flight promise and a "dirty again" flag so a change during a
  // save schedules exactly one follow-up save when the current one settles —
  // never cancelling the user's edits, never reloading the doc.
  const savingRef = useRef(false);
  const dirtyAgainRef = useRef(false);

  const persist = useCallback(async () => {
    if (!documentId || mode === 'viewing') return;
    // Coalesce: if a save is already running, mark dirty and let it re-run once.
    if (savingRef.current) {
      dirtyAgainRef.current = true;
      return;
    }
    savingRef.current = true;
    try {
      // save() is READ-ONLY on the live view (serializes PM state → bytes); it
      // does not reset the doc or cursor, so typing continues uninterrupted.
      const buffer = await editorRef.current?.save?.();
      if (buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
        const body = btoa(binary);
        await updateDocumentTemplateDraft({
          documentTemplateId: String(documentId),
          template: { body, body_storage_format: 'docx' } as any,
        })(apolloClient);
        // Report the save time so the header's "Last Saved" advances WITHOUT a
        // template refetch. A refetch would return the just-saved body and
        // reload the editor's documentBuffer mid-edit — cancelling the user's
        // in-progress work. This lightweight callback avoids that entirely.
        onDraftSaved?.();
      }
    } catch {
      /* autosave errors are non-fatal; the editor keeps the live doc */
    } finally {
      savingRef.current = false;
      // A change arrived while saving → run exactly one more save now.
      if (dirtyAgainRef.current) {
        dirtyAgainRef.current = false;
        void persist();
      }
    }
  }, [apolloClient, documentId, mode, onDraftSaved]);

  // Sidebar panels (placeholder / clause) live OUTSIDE the editor and read it via
  // the registry; notify them on every content change so their lists stay live.
  const changeListenersRef = useRef(new Set<() => void>());
  const subscribeToChanges = useCallback((listener: () => void) => {
    changeListenersRef.current.add(listener);
    return () => changeListenersRef.current.delete(listener);
  }, []);

  const handleChange = useCallback(() => {
    onDocumentChange?.();
    changeListenersRef.current.forEach((l) => l());
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void persist();
      // Reconcile relational suggestion rows from the same live PM state, then
      // tell the parent the pending count so the publish gate re-reads.
      const view = editorRef.current?.getEditorRef?.()?.getView?.();
      // Merged body + header/footer list — see DocxSuggestionSyncHandle.sync.
      void suggestionSync
        .sync(editorRef.current?.getTrackedChanges?.() ?? null)
        .then((count) => onSuggestionChange?.(count));
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [persist, onDocumentChange, suggestionSync, onSuggestionChange]);

  // ── Save version ──────────────────────────────────────────────────────────
  // Same contract as the deal editor — one polymorphic mutation, one snapshot
  // format. Flush the draft first so the version matches what's on screen.
  const [saveVersionMutation] = useMutation(mutations.saveDocumentVersion);
  const handleSaveVersion = useCallback(async () => {
    if (!documentId) return;
    const name = window.prompt('Name this version (optional):');
    if (name === null) return;
    try {
      await persist();
      await saveVersionMutation({
        variables: {
          documentId: String(documentId),
          documentType: 'DocumentTemplate',
          name: name.trim() || null,
          event: 'manual',
        },
      });
    } catch {
      /* surfaced by the mutation's error link */
    }
  }, [documentId, persist, saveVersionMutation]);

  // ── Import new version (File → "Import new version…") ─────────────────────
  // Mirrors the deal editor: snapshot the OUTGOING draft as a version BEFORE
  // the imported file replaces it, then persist the new body as the working
  // draft and refetch so the editor remounts on it (the imported version
  // becomes the current draft you keep editing).
  const handleImportNewVersion = useCallback(
    async (file: File) => {
      if (documentId) {
        try {
          await persist();
          await saveVersionMutation({
            variables: {
              documentId: String(documentId),
              documentType: 'DocumentTemplate',
              name: `Before import — ${new Date().toLocaleString()}`,
              event: 'import',
            },
          });
        } catch {
          notifyError?.({
            message: "Couldn't save a version before importing. The import will continue.",
          });
        }
      }
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
        const newBody = btoa(binary);
        // Remember the exact body we're importing; when the refetched source
        // carries it, the effect above bumps reloadKey → the editor re-resolves
        // onto the imported version immediately (no leave/return required).
        pendingImportBodyRef.current = newBody;
        await updateDocumentTemplateDraft({
          documentTemplateId: String(documentId),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          template: { body: newBody, body_storage_format: 'docx' } as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)(apolloClient);
        // Refetch so the page re-passes the new body to this container.
        onRefetchTemplate?.();
        notifySuccess?.({ message: 'New version imported.' });
      } catch {
        notifyError?.({ message: 'Could not import that file. Please try again.', pinned: true });
      }
    },
    [documentId, persist, saveVersionMutation, apolloClient, onRefetchTemplate, notifyError, notifySuccess]
  );

  // Register this editor so sibling sidebar panels can enumerate/scroll its
  // placeholders + insert new ones (docx-native replacement for `window.editor`).
  const insertPlaceholderRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!documentId) return;
    const handle = makeHandleFromRef(editorRef, subscribeToChanges, () => insertPlaceholderRef.current());
    return registerDocxNativeEditor(String(documentId), handle);
  }, [documentId, subscribeToChanges]);

  // Insert a PropDocs construct (placeholder / checkbox) at the cursor. Builds
  // the native content-control (SDT) node via the editor's factories and
  // dispatches it through the live PM view — the same path the prototype uses.
  const insertConstruct = useCallback((kind: 'placeholder' | 'checkbox') => {
    const view = editorRef.current?.getEditorRef?.()?.getView?.() as any;
    if (!view) return;
    const { schema, tr } = view.state;
    if (kind === 'checkbox') {
      const node = buildCheckboxNode(schema);
      if (node) view.dispatch(tr.replaceSelectionWith(node, false).scrollIntoView());
      view.focus();
      return;
    }
    // Placeholder: insert with a default name, then move the cursor INTO it so
    // the PlaceholderPopup (which polls the selection) opens for naming — no
    // window.prompt. The user edits Name/Required in the styled balloon.
    const node = buildPlaceholderNode(schema, { name: 'Field Name', required: false });
    if (node) {
      const insertPos = tr.selection.from;
      let insertTr = tr.replaceSelectionWith(node, false);
      // Place the caret INSIDE the just-inserted SDT (its content starts at
      // insertPos + 1) so the placeholder popup opens for naming.
      try {
        insertTr = insertTr.setSelection(TextSelection.near(insertTr.doc.resolve(insertPos + 1)));
      } catch {
        /* keep default selection if resolve fails */
      }
      view.dispatch(insertTr.scrollIntoView());
      // No editor click occurred (the button was in the toolbar/sidebar), so
      // explicitly ask the click-gated popup to open for naming.
      requestPlaceholderPopupOpen();
    }
    view.focus();
  }, []);

  // Keep the registry's insert action pointed at the live insertConstruct so the
  // sidebar "+" button inserts a placeholder at the cursor.
  useEffect(() => {
    insertPlaceholderRef.current = () => insertConstruct('placeholder');
  }, [insertConstruct]);

  // Restricted-editing authoring: in a restricted template, "Make editable"
  // marks the selection as an editable exception in the otherwise-frozen doc.
  const onMakeEditable = useCallback(() => {
    // Route to the ACTIVE view — when a header/footer is being edited that's the
    // H/F PM, otherwise the body. Using getEditorRef().getView() (body only)
    // wrapped the body doc with the header's selection offsets, so "Enable
    // editing" inside a header did nothing (no highlight).
    const view = editorRef.current?.getActiveEditorView?.() as any;
    if (view) makeSelectionEditable(view);
  }, []);

  // Tells the floating bubble whether the current selection is already editable,
  // so its label toggles Enable ↔ Disable (the bubble reads this per-selection).
  const isSelectionEditable = useCallback((range: { from: number; to: number }) => {
    // Same active-view routing as onMakeEditable — the range is resolved against
    // whichever region (body or H/F) is currently being edited.
    const view = editorRef.current?.getActiveEditorView?.() as any;
    return isRangeEditable(view ?? null, range.from, range.to);
  }, []);

  // Insert a reusable clause at the cursor via the editor's parseDocx→PM
  // pipeline (`insertDocxFragment`), preserving formatting/lists/placeholders/
  // checkboxes. Two source formats:
  //   - 'docx' — the clause body is already base64 .docx (the format all clauses
  //     save in post-migration); decode to bytes and insert directly.
  //   - 'html' — a legacy CKEditor-HTML clause; convert to .docx on the fly with
  //     the same browser-safe migration converter used for un-migrated bodies.
  const insertClause = useCallback(
    async (payload: ClauseDndPayload): Promise<boolean> => {
      const { format, body } = payload;
      if (!body) return false;
      let buffer: Uint8Array;
      if (format === 'docx') {
        const { base64ToBytes } = await import('./clauseBody');
        buffer = base64ToBytes(body);
      } else {
        if (!body.trim()) return false;
        const { convertHtmlToDocx } = await import('../../../../utils/docx-migration-v2');
        buffer = (await convertHtmlToDocx(body, window.DOMParser)).buffer;
      }
      const inserted = (await editorRef.current?.insertDocxFragment?.(buffer)) ?? false;
      if (inserted) handleChange(); // trigger autosave for the new content
      return inserted;
    },
    [handleChange]
  );

  // Clause drop = native HTML5 drag/drop, one system end-to-end (no react-dnd).
  // The clause sidebar item sets a typed payload (CLAUSE_DND_MIME → JSON envelope
  // {format, body}) on dragstart; here we accept it, show the copy cursor, move
  // the caret to the point under the pointer, and insert there.
  //
  // The editor's OWN caret is hidden while the editor isn't focused (and it
  // isn't during a drag — the drag source has focus), so we paint our own
  // drop-caret (`|`) at the target position to show exactly where it will land.
  const [dropCaret, setDropCaret] = useState<{ top: number; left: number; height: number } | null>(null);

  const moveCaretToPoint = useCallback((clientX: number, clientY: number) => {
    const paged = editorRef.current?.getEditorRef?.() as any;
    const pos = paged?.positionFromCoords?.(clientX, clientY);
    if (typeof pos !== 'number') return;
    paged?.setSelection?.(pos);
    // Read the painted caret element's rect (it moves with the selection even
    // while hidden) to place our visible drop-caret exactly there. `window.`
    // because the `document` PROP shadows the global here.
    const caretEl = window.document.querySelector<HTMLElement>('[data-testid="caret"]');
    if (caretEl) {
      const r = caretEl.getBoundingClientRect();
      if (r.height) setDropCaret({ top: r.top, left: r.left, height: r.height });
    }
  }, []);

  const onClauseDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      // Only react to clause drags (not text selections etc.). The custom MIME
      // is present in `types` throughout the drag even when Chrome hides the
      // value until drop.
      if (!e.dataTransfer.types.includes(CLAUSE_DND_MIME)) return;
      e.preventDefault(); // required for the drop to be allowed + copy cursor
      e.dataTransfer.dropEffect = 'copy';
      moveCaretToPoint(e.clientX, e.clientY);
    },
    [moveCaretToPoint]
  );

  const onClauseDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Only clear when the pointer actually leaves the container (not when moving
    // between inner children — those fire dragleave with relatedTarget inside).
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropCaret(null);
  }, []);

  const onClauseDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const payload = decodeClauseDnd(e.dataTransfer.getData(CLAUSE_DND_MIME));
      if (!payload) return; // not a clause drop
      e.preventDefault();
      setDropCaret(null);
      moveCaretToPoint(e.clientX, e.clientY); // insert at the drop point
      void insertClause(payload);
    },
    [moveCaretToPoint, insertClause]
  );

  // Push the template's restricted context into the plugin once the view exists.
  // Template AUTHORING uses the 'template' context (fully editable + can mark
  // exceptions); a non-restricted template uses 'off'.
  const restrictContext = isRestrictedEditingEnabled ? 'template' : 'off';
  const onEditorReady = useCallback(() => {
    const view = editorRef.current?.getEditorRef?.()?.getView?.() as any;
    if (view) setRestrictedContext(view, restrictContext);
  }, [restrictContext]);
  // Re-apply the context if `isRestrictedEditingEnabled` flips AFTER mount — the
  // onEditorReady callback only fires once at view creation.
  useEffect(() => {
    const view = editorRef.current?.getEditorRef?.()?.getView?.() as any;
    if (view) setRestrictedContext(view, restrictContext);
  }, [restrictContext]);

  // While an un-migrated HTML body is being converted, hold the editor mount so
  // it doesn't flash an empty doc and then swap content underneath the user.
  if (resolving) {
    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        Preparing document…
      </div>
    );
  }

  return (
    <div
      // `is-restricted-active` scopes the restricted-region highlight/affordance:
      // when the restricted flag is OFF the propdocs:restricted SDTs are dormant
      // (no highlight, no tab-stop) — they stay in the body but read as normal text.
      className={isRestrictedEditingEnabled ? 'is-restricted-active' : undefined}
      style={{ height: '100%', width: '100%' }}
      onDragOver={onClauseDragOver}
      onDrop={onClauseDrop}
      onDragLeave={onClauseDragLeave}
      onDragEnd={() => setDropCaret(null)}
    >
      {/* Drop-caret: a visible "|" showing exactly where a dragged clause will
          land (the editor's own caret is hidden while it isn't focused). */}
      {dropCaret && (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            top: dropCaret.top,
            left: dropCaret.left,
            width: 2,
            height: dropCaret.height,
            background: '#006c90',
            zIndex: 6000,
            pointerEvents: 'none',
            animation: 'propdocs-drop-caret-blink 1s steps(2, start) infinite',
          }}
        />
      )}
      <style>{'@keyframes propdocs-drop-caret-blink{50%{opacity:0}}'}</style>
      <DocxEditor
        ref={editorRef}
        {...(document ? { document } : documentBuffer ? { documentBuffer } : { document: createEmptyDocument() })}
        mode={mode}
        lockMode={modeLocked}
        author={author}
        mentionableUsers={mentionableUsers}
        onChange={handleChange}
        onSaveVersion={handleSaveVersion}
        onOpen={handleImportNewVersion}
        showToolbar
        showRuler
        // Zoom is hidden — zooming the canvas mis-aligns overlays (comments,
        // caret, H/F chrome), so PropDocs keeps the template editor at 100%.
        showZoomControl={false}
        // TEMPLATE AUTHORING: `disable_word_export` is metadata for the DEAL
        // DOCUMENTS made from this template — same rule as the header/footer lock
        // below. Someone who can edit the template already has its full content,
        // so withholding "Download DOCX" from them protects nothing and only
        // blocks the normal authoring loop (round-trip through Word, hand a copy
        // to counsel). Read-only viewers still respect the flag.
        disableDocxExport={canEdit ? false : !!disableWordExport}
        // Header/footer independent lock (🔒 badge + can't enter to edit).
        // NOT gated on `restricted_editing`: the H/F lock is its OWN flag, exactly
        // as it was in CKEditor (the Add Header/Footer modal wrote and read
        // `header_restricted_editing`/`footer_restricted_editing` directly, with
        // no restricted-editing precondition). Gating it made the toggle a silent
        // no-op on every non-restricted template — state flipped, mutation
        // persisted, prop stayed false, so no badge/chip ever appeared (e.g. 114:
        // header_restricted_editing=true, restricted_editing unset).
        headerLocked={headerLocked}
        footerLocked={footerLocked}
        // TEMPLATE AUTHORING: the lock is metadata for the deal documents made
        // from this template — the author must keep editing the H/F freely. The
        // badge reads "locked for deal documents" and the Options-menu toggle
        // stays reachable (with enforcement on, a locked H/F could never open
        // its own editor, making the state confusing and near-irreversible).
        headerFooterLockEnforced={false}
        onToggleHeaderFooterLock={onToggleHeaderFooterLock}
        onRequestHeaderFooterUnlock={onRequestHeaderFooterUnlock}
        // Remove the default document icon left of the File menu (PropDocs has
        // its own template chrome above the editor).
        renderLogo={() => null}
        externalPlugins={externalPlugins}
        onEditorViewReady={onEditorReady}
        // Restricted-template authoring: the floating selection bubble's
        // enable-editing action marks a region editable in the deal doc.
        makeEditableEnabled={isRestrictedEditingEnabled}
        onMakeEditable={onMakeEditable}
        isSelectionEditable={isSelectionEditable}
        onModeChange={() => {
          /* template mode is pinned (suggesting); ignore user toggles */
        }}
        onInsertCheckbox={() => insertConstruct('checkbox')}
        onInsertPlaceholder={() => insertConstruct('placeholder')}
      />
      {/* Click a placeholder → styled Name/Required balloon (polls PM selection,
          anchors at the painted caret). Replaces the window.prompt insert UX. */}
      <PlaceholderPopup editorRef={editorRef} onEdited={handleChange} />
    </div>
  );
};
